import events from 'packages/alcumus-local-events'

import debounce from 'lodash/debounce'

import { showNotification } from 'common/modal'
import { process as _process } from 'common/process'
import { store as globalStore } from 'common/global-store'

import { parse } from './search-parser'
import { raw, store } from './store'

import { buildSearchLists } from '../lib/search-query-lists'
import { buildSearchOptions } from '../lib/search-query-options'
import { getSearchExtra } from '../lib/search-query-has-impact'

let lastSearch = 'unlikely'
let lastRows = -1
let lastOption = 'unlikely'
let lastLists = ''
let queued = false

const LIST_HYDRATION = {
    id: true,
    name: true,
    coRef: true,
    companyTradingName: true,
    verificationStatus: true,
    supplierQuestionnaireData: {
        services: true,
    },
    supplierPolicies: {
        hasBusinessContinuityPlan: true,
        hasGDPRPolicy: true,
        hasInformationSecurityPolicy: true,
        hasAntiBriberyPolicy: true,
        hasEnvironmentalPolicy: true,
        hasHandSPolicy: true,
        hasEthicalPurchasingPolicy: true,
        hasSafeguardingPolicy: true,
        hasQualityPolicy: true,
        hasModernSlaveryPolicy: true,
        hasEqualityDiversityPolicy: true,
    },
    supplierISOCerts: {
        hasISO9001: true,
        hasISO45001: true,
        hasISO27001: true,
        hasISO14001: true,
    },
    type: true,
    scAccreditationExpiryDate: true,
    approvalDate: true,
    scAuditStatus: true,
    workCategories: true,
    contactDetails: {
        title: true,
        firstname: true,
        surname: true,
        address: true,
        email: true,
        landline: true,
        mobile: true,
        website: true,
    },
    pqqProperties: {
        financialRating: true,
        pqqStage: true,
    },
    pqq: true,
    atRisk: true,
    renewalExpiredRisk: true,
    insuranceRisk: true,
    accredited: true,
    disengaged: true,
    suspended: true,
    initial: true,
    inProgress: true,
    insurancesExpired: true,
    ssipExpired: true,
    paymentMembershipExpired: true,
    retired: true,
    crn: true,
    status: true,
    ragStatus: true,
    alias: true,
    owner: true,
    latLng: true,
    tweet: true,
    anchored: true,
    modernSlavery: true,
    gdpr: true,
    envPolicy: true,
    qualPolicy: true,
    antiBribery: true,
    iso9001: true,
    iso14001: true,
}

async function runSearchQuery(isFromDashboard = false) {
    if (store.searching$) {
        return (queued = true)
    }

    let searchItem = store.search?.value?.input?.value?.trim() || ''
    let newSearch

    // escape single quotes that are part of words
    // searchItem = searchItem.replace(/([a-z]+)(')([a-z]+)/ig,"$1\"'\"$3");

    const aposRegex = /([a-z]+)(')([a-z]+)/gi
    const parenRegex = /\((.+?)\)/g

    if (searchItem.length > 0 && !searchItem.includes(':')) {
        newSearch =
            searchItem.replace(aposRegex, '$1"\'"$3').replace(parenRegex, '"($1)"') +
            " OR(name:'" +
            searchItem.replace(aposRegex, "$1\\'$3") +
            "')"
    } else {
        newSearch = searchItem
    }

    let displayRows = store.settings?.value?.displayRows?.value || ''
    let sortSetting = store.settings?.value?.sort?.value || ''
    let { location, suppliers, contractors } = store.options$
    let sortOption = sortSetting
    const searchExtra = getSearchExtra()

    if (!newSearch && !searchExtra.impact) {
        if (!location) {
            sortOption = 'name'
        }
    }

    sortOption = newSearch && newSearch !== lastSearch ? (location ? '-s' : '-v') : sortOption

    if (!location && ['-s', 't', 'd'].includes(sortOption)) {
        sortOption = newSearch ? '-v' : 'name'
    }

    store.settings$.set({ sort: sortOption })

    if (!searchExtra.change && newSearch === lastSearch && displayRows === lastRows && sortOption === lastOption) {
        return
    }

    try {
        if (newSearch.trim()) parse(newSearch)
    } catch (e) {
        console.info(`Search query ${newSearch} failed parsing: ${e.message}`)
        return
    }

    const lists = {}
    buildSearchLists(lists)
    let listCompare = JSON.stringify(lists)
    if ((newSearch !== lastSearch || listCompare !== lastLists) && lastSearch !== '<unlikely>') {
        store.display.value.firstRow = 1
    }
    lastLists = listCompare
    lastOption = sortOption
    lastSearch = newSearch
    lastRows = displayRows

    const roleSupplierFilter =
        '(role:"supplier") (status:"atrisk" OR status:"accredited" OR status:"inprogress" OR status:"insurancerisk" OR status:"renewalexpiredrisk" OR status:"clientapproved")'
    let baseSearch =
        '(status:"atrisk" OR status:"accredited" OR status:"inprogress" OR status:"insurancerisk" OR status:"renewalexpiredrisk" OR status:"clientapproved")'
    if (window._env_.REACT_APP_TURN_ON_SS_DATA === 'true') {
        if (suppliers && contractors) {
            baseSearch =
                '(status:"atrisk" OR status:"accredited" OR status:"inprogress" OR status:"insurancerisk" OR status:"renewalexpiredrisk" OR status:"clientapproved" OR type:"supplier")'
        } else if (suppliers && !contractors) {
            baseSearch = `${roleSupplierFilter} OR type:"supplier"`
        } else if (!suppliers && contractors) {
            baseSearch =
                '(status:"atrisk" OR status:"accredited" OR status:"inprogress" OR status:"insurancerisk" OR status:"renewalexpiredrisk" OR status:"clientapproved")'
        } else {
            store.set({
                results: {
                    rowCount: 0,
                },
                bitmaps: {},
                rowCount: {},
                hideResults: false,
            })
            events.emit('search-finished', null)
            return
        }
    }

    if (!lists.__onsafecontractor) {
        if (lists['__oncompany size']) {
            lists.__oncompanysize = {
                use: true,
                search: baseSearch,
            }
        } else {
            lists.__onsafecontractor = {
                use: true,
                search: contractors && !isFromDashboard ? `${baseSearch} OR status:prospect` : baseSearch,
            }
        }
    }
    try {
        store.set({
            searching: Math.max(0, store.searching$) + 1,
            selected: raw({}),
        })
        const starMap = globalStore?.$profile?.starList || {}
        const isMyListSelected = Object.hasOwn(lists, '__anchored') ? lists.__anchored : store.$options.__anchored
        let searchOptions = {
            take: displayRows,
            skip: (store.display.value.firstRow.value || 1) - 1,
            search: newSearch ? newSearch : '',
            sort: sortOption,
            hydrate: LIST_HYDRATION,
            tabulations: {},
            type: 'safecontractor.contractors',
            lists,
            starList: starMap,
            prioritizeStarList: !!isMyListSelected,
            // dontCountLists: true,
        }
        buildSearchOptions(searchOptions)
        store.set({ searchOptions })
        events.emit('search-will-search', searchOptions)

        let result = await _process({
            type: 'contractors.search', //store.$results
            search: searchOptions,
        })

        if (!result.client) {
            events.emit('search-error', result)
            console.error(result)
        } else {
            store.set({
                results: result.client.result, // store.$results
                bitmaps: result.client.result.bitmaps,
                rowCount: result.client.result.rowCount,
            })
            events.emit('search-finished', result.client)
        }
        events.emit('search-performed')
    } catch (e) {
        console.error(e)
        showNotification('An error occurred running the search')
    } finally {
        store.set({
            searching: Math.max(0, store.searching$ - 1),
        })
    }
    if (queued) {
        queued = false
        await runSearchQuery(isFromDashboard)
    } else {
        store.set({ hideResults: false })
    }
}

const _search = debounce(runSearchQuery, 700)

export function search(force, isFromDashboard) {
    if (force) {
        lastRows = -1
    }
    store.set({ hideResults: true })
    return _search(isFromDashboard)
}

export function cancelSearch() {
    _search.cancel()
}

export function searchNow(force) {
    if (force) {
        lastRows = -1
    }
    _search()
    setTimeout(() => _search.flush())
}
